/**
 * 퀘스트 스팟 — 스팟 태블릿 (현장 미션 러너)
 * ============================================
 * 설계: docs/2026-06-08-quest-coupon-design.md §6.2 / dev_steps P5
 *       docs/2026-06-19-quest-spot-mission-types-design.md §9.2 (미션 러너 일반화) / dev-steps S6
 *
 * 흐름:
 *   1) 진입 시 GET /tools/quest/setup → active 세트의 active 스팟 목록(각 스팟에
 *      mission_type / mission_config / mission_config_version 포함).
 *      마이그160a 이후 각 스팟에 is_first(첫 미션 판정의 **단일 권위**) / intro_video_url 과
 *      OX 퀴즈용 mission_video_url 이 함께 온다.
 *      active_set 없으면 "진행 중인 퀘스트가 없습니다" 안내.
 *   2) 운영자가 이 태블릿이 담당할 스팟 1개를 선택 → localStorage 영속(새로고침 유지).
 *      헤더의 스팟 이름 클릭으로 active 스팟 목록 팝업에서 재선택 가능.
 *   3) 회원 QR 식별(3종 공통 선행) 후 미션 종류로 분기:
 *      - qr          : 즉시 POST /tools/quest/tag → 응답 already_tagged 로 D9 메시지.
 *      - ox_quiz     : check 선행 → 이미참여=D9 메시지 / 신규=선택 영상 재생 → 문항 풀에서
 *                      무작위 1개 O/X(영상 없음=즉시 표시, 클라 즉시채점) → tag(mission_result).
 *      - picture_quiz: check 선행 → 문제·선택 영상 재생 → 영상 3개 중 하나 선택 → tag.
 *      - funny_photo : check 선행 → 이미참여=D9 메시지 / 신규=전면 카메라 캡처(무저장 D4) → tag {captured:true}.
 *
 * D9 재참여 3분기 메시지(설계 §9.2):
 *   already_tagged===false                  → 🌿 {스팟명} 퀘스트 완료! ({done}/{total})  (신규 완료)
 *      ⚠️ 2026-08-28: **미션 3종 완료 직후에는 이 오버레이·완료음을 띄우지 않는다**
 *         (일반 QR 태깅만 유지). 판정 = `shouldShowTagFeedback` / `shouldPlayCompleteSoundForTag`.
 *   already_tagged===true && done <  total  → 이미 참여했어, 다음 미션으로 고고~!         (다른 스팟 유도)
 *   already_tagged===true && done === total → 이미 참여했어~!                            (전체완료 팝업)
 *
 * 카메라 패턴: QrStickerReaderPage(카메라) = QRScanner 컴포넌트 재사용(회원 QR 식별).
 *   웃긴표정 캡처는 별도 FunnyPhotoRunner(전면 카메라, getUserMedia)에서 처리.
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// 상단 헤더를 대체하는 숨은 관리자 바 — 오른쪽 아래 5회 터치 → 비밀번호 → 타이틀바.
// 고객 앞에 놓이는 태블릿이라 조작 UI 를 노출하지 않는다(2026-07-30 요청).
// ⚠️ 지점명(StoreBadge)은 이 화면에서 제거했다 — 고객에게 불필요하다는 사용자 판단.
import QuestSpotAdminBar from '../components/quest/QuestSpotAdminBar';
// S1 이 옛 8초 지도 경로와 함께 뺐던 것을 S7 이 **새 경로**(인사 뒤 분기)로 되살린다.
import MissionStatusMap, { canRenderStatusMap } from '../components/quest/MissionStatusMap';
// 스캔 결과 전체화면 안내판 4초 — 이미완료(Figma 2937:39753) / 등록되지않음(2936:39627).
// ⚠️ 렌더는 renderBody() 가 아니라 **최상위 .qt-page 직계**다(전체화면 보장 — 아래 주석 참조).
import MissionNoticeBoard, {
  NOTICE_ALREADY_DONE, NOTICE_UNREGISTERED, NOTICE_INVALID_QR, NOTICE_FIRST_MISSION_FIRST,
  NOTICE_INVALID_MEMBER_QR,
  // 여권 QR 4종 + 파싱실패 1종 (2026-09-01, 설계 §4.8) — 기존 5종 문구는 불변이다.
  NOTICE_PASSPORT_UNREGISTERED, NOTICE_PASSPORT_EXPIRED, NOTICE_PASSPORT_CANCELLED,
  NOTICE_PASSPORT_UNUSABLE, NOTICE_UNPARSABLE,
} from '../components/quest/MissionNoticeBoard';
import OxQuizMissionFlow, {
  resolveMissionVideoUrl,
} from '../components/quest/OxQuizMissionFlow';
import PictureQuizMissionFlow, {
  resolvePictureQuizIntroUrl,
} from '../components/quest/PictureQuizMissionFlow';
import { resolveTimeoutSec } from '../components/quest/FunnyPhotoRunner';
import FunnyPhotoMissionFlow from '../components/quest/FunnyPhotoMissionFlow';
import PhotoCameraToolbar from '../components/quest/PhotoCameraToolbar';
import { readPhotoCamera, savePhotoCamera } from '../components/quest/photoCamera';
import useUsbQrReader from '../hooks/useUsbQrReader';
import { formatQuestSetReward } from '../utils/questRewards';
import useQuestVideoWatchdog from '../hooks/useQuestVideoWatchdog';
import { extractQuestScanCode } from '../utils/qrUtils';
import {
  getQuestSetup, tagQuestSpot, checkQuestSpot, reportQuestIntroVideoError, fetchQuestMissionMap,
} from '../services/api';
import { questSpotNameKey } from '../constants/screenLabels';
import { QrcodeOutlined } from '@ant-design/icons';
// OX 대기 화면의 대형 타이틀 워드마크 (Figma 3563:43250, 2026-08-12 취득).
// 배경·로고와 달리 CSS background 가 아니라 <img> 로 싣는다 — 렌더 가드가 src 를 볼 수 있고
// alt 로 대체 텍스트를 줄 수 있다(CSS 배경은 둘 다 불가).
import missionCompleteTitle from '../assets/quest/mission_complete_title.webp';
import './QuestTagPage.css';

// 기존 테스트·소비처의 import 경로를 유지하면서 실제 구현은 미리보기와 공유한다.
export {
  default as OxQuizMissionFlow,
  OX_PHASE_INTRO,
  OX_PHASE_GREETING,
  OX_PHASE_QUIZ,
  initialOxPhase,
  nextOxPhase,
  resolveMissionVideoUrl,
  resolveOxGreetingVideoUrl,
} from '../components/quest/OxQuizMissionFlow';

const COOLDOWN_MS = 2500; // 같은 회원 QR 연속 스캔 차단
export const SUCCESS_DISPLAY_MS = 2500; // 완료 메시지 노출 시간 (읽을 시간 확보)
// 전체화면 안내판(이미완료·등록되지않음) 노출 시간 — 사용자 지정 **4초**(3초 → 5초 → 4초).
// 두 안내판이 **같은 시간**을 쓴다(2026-08-04 사용자 확정: "안내판 2종 모두 4초").
// 종류별로 쪼개지 않는다 — 쓰지 않을 분기가 생긴다.
export const NOTICE_DISPLAY_MS = 4000;
export const FIRST_MISSION_GREETING_MS = 3000;
/** 첫 미션 재태그 시 미션 상태 지도 노출 시간 — 지도에서 남은 스팟을 찾을 시간이 필요하다. */

// ── 첫 미션 인트로 영상 (기획 §4.4·§4.5 / dev-steps S13) ────────────────────────
// 🔴 하드코딩 영상 URL 상수와 20초 고정 상한 상수는 **삭제됐다**(성공기준 3).
//    URL 은 지점별로 다르므로 서버가 `intro_video_url` 로 내려주고(§4.4),
//    상한은 D4("끝까지 시청")와 정면 충돌하므로 없앴다.
//    ⚠️ 지워진 두 상수의 옛 이름을 이 파일 어디에도(주석 포함) 되살리지 마라 —
//       dev-steps S13 DoD 가 그 이름을 **grep 0건**으로 검증한다.
//
// 🔴 그 20초 상한이 **현행 유일한 탈출구**였다. 그냥 없애면 `onPlaying` 뒤 정지했을 때
//    `spotPickLockReason` 이 `firstMissionIntro` 때문에 잠겨 **미션도 못 바꾸고 앱 재시작 외
//    복구 불가**가 된다. 그래서 워치독 **2종**으로 대체한다 — 감시 구간이 겹치지 않아
//    둘 다 필요하다.
//      · 로딩 워치독   : 마운트 → `onPlaying`.  재생이 시작조차 못 하고 `onError` 도 안 오는 상태.
//      · 무진행 워치독 : `onPlaying` → `onEnded`. 재생 중 디코더·네트워크 정지.
//
// 🔴 무진행 감지를 `timeupdate`/`progress` **이벤트 핸들러로 만들면 안 된다** — 정지하면
//    이벤트 발화가 끊겨 판정 코드가 영원히 실행되지 않는다. 그래서 `setInterval` 폴링으로
//    `video.currentTime` 스냅샷을 비교한다. 정상 재생은 `currentTime` 이 계속 늘어 절대
//    잘리지 않는다(D4 준수).
// 🔴 **정의는 `hooks/useQuestVideoWatchdog.js` 로 옮겼다**(2026-08-12). OX 수행 흐름 3화면이
//    같은 재생 계약을 쓰게 되어 워치독을 훅으로 추출했고, 상수는 그 훅과 붙어 있어야
//    한쪽만 바뀌는 사고가 안 난다. 여기서는 **재수출만** 한다 —
//    기존 소비처(QuestTagPage.test.js)의 import 경로를 깨지 않기 위해서다.
export {
  INTRO_VIDEO_LOAD_TIMEOUT_MS,
  INTRO_VIDEO_STALL_TICK_MS,
  INTRO_VIDEO_STALL_LIMIT_TICKS,
  INTRO_VIDEO_REASON_LOAD_TIMEOUT,
  INTRO_VIDEO_REASON_STALL,
  INTRO_VIDEO_REASON_ERROR,
} from '../hooks/useQuestVideoWatchdog';

const STORE_KEY_PREFIX = 'quest_spot:'; // 매장 단위로 스팟 바인딩 보존
const MISSION_COMPLETE_SOUND_SRC = `${process.env.PUBLIC_URL || ''}/sounds/mission-complete.m4a`;
const MISSION_REJECTED_SOUND_SRC = `${process.env.PUBLIC_URL || ''}/sounds/mission-rejected.m4a`;

// 러너 단계(ox_quiz/picture_quiz/funny_photo) — 회원 식별 후 미션 수행 중 화면.
const PHASE_SCAN = 'scan'; // 회원 QR 식별 대기
const PHASE_MISSION = 'mission'; // 미션 수행 중(ox/funny)

/**
 * 태블릿 설정 **무음 재조회** 주기.
 *
 * 🔴 없으면 CRM 변경이 태블릿에 **영원히 닿지 않는다.** `loadSetup` 은 마운트 시 1회만 돌고
 *    태블릿은 며칠씩 같은 화면에 떠 있다. 2026-08-20 실측 사고: OX 결과 영상 4종을 18:13에
 *    배정했는데 태블릿은 16:25 진입 시점의 config 를 들고 있어 레거시 영상으로 폴백했다.
 * 60초 — 운영자가 CRM 을 고치고 현장으로 걸어가는 시간보다 짧고, 유휴일 때만 도는 GET 이라
 * 부담이 없다(하트비트가 이미 20초 주기로 돈다).
 */
const SETUP_REFRESH_MS = 60000;

/** 같은 퀘스트 세트의 OX 공통 정·오답 영상 설정을 그림퀴즈 연출에 재사용한다. */
export function findSharedOxVerdictConfig(spots) {
  if (!Array.isArray(spots)) return null;
  const verdictKeys = [
    'correct_video_url', 'incorrect_video_url',
    'o_correct_video_url', 'o_incorrect_video_url',
    'x_correct_video_url', 'x_incorrect_video_url',
  ];
  const oxSpot = spots.find((spot) => spot?.mission_type === 'ox_quiz'
    && verdictKeys.some((key) => typeof spot?.mission_config?.[key] === 'string'
      && spot.mission_config[key].trim()));
  return oxSpot?.mission_config || null;
}

/** 같은 퀘스트 세트의 OX 시작 영상을 그림퀴즈 시작 연출의 fallback으로 사용한다. */
export function findSharedOxIntroVideoUrl(spots) {
  if (!Array.isArray(spots)) return null;
  const oxSpot = spots.find((spot) => spot?.mission_type === 'ox_quiz'
    && typeof spot?.mission_video_url === 'string'
    && spot.mission_video_url.trim());
  return oxSpot ? oxSpot.mission_video_url.trim() : null;
}

export function resolveCommonLogoIntroUrl(activeSet) {
  const candidate = activeSet?.reward_presentation?.logo_intro_video_url;
  return typeof candidate === 'string' && candidate.trim() ? candidate.trim() : null;
}

// HTTP(비-localhost) = 보안 컨텍스트 아님 → 브라우저가 카메라 차단. 이땐 카메라를 숨기고
// USB 리더기 안내만 띄운다(현장 태블릿은 USB 리더기 사용). QRScanner.js 의 isDevMode 와 동일 조건.
const CAMERA_BLOCKED =
  typeof window !== 'undefined' &&
  window.location.protocol === 'http:' &&
  window.location.hostname !== 'localhost';

// 매장별 키 — 토큰 매장 전환 시 다른 스팟이 잘못 복원되지 않도록 store_id 로 네임스페이스.
function spotStorageKey() {
  const storeId = localStorage.getItem('store_id') || 'unknown';
  return `${STORE_KEY_PREFIX}${storeId}`;
}

// 담당 스팟 이름을 localStorage 에 동기화 — device-manager 현재화면 라벨 보강용(useHeartbeat 가 읽음).
// name 있으면 저장, 없으면(null/'') 삭제. 키 출처=screenLabels.questSpotNameKey(매장 네임스페이스).
function persistSpotName(name) {
  try {
    if (name) localStorage.setItem(questSpotNameKey(), name);
    else localStorage.removeItem(questSpotNameKey());
  } catch {
    /* 저장 실패해도 동작 무영향 — 라벨만 기본값 폴백 */
  }
}

// 스팟 보상 지급 문구(팝업 덧붙임, 마이그143). granted=true(신규 지급)일 때만 표기 —
// granted=false(무보상/멱등/지급실패)면 빈 문자열(§4.5 계약). 복구 지급(재태깅 granted=true)도 표기.
function formatSpotRewardMsg(spotReward) {
  if (!spotReward || !spotReward.granted) return '';
  if (spotReward.reward_type === 'coin' && spotReward.coin_amount != null) {
    return ` · 주렁주화 ${spotReward.coin_amount}개 적립!`;
  }
  if (spotReward.reward_type === 'coupon') {
    return ` · ${spotReward.coupon_name || '쿠폰'} 쿠폰 도착!`;
  }
  return '';
}

// D9 3분기 메시지 — tag 응답 또는 check 결과의 already_tagged + done/total 로 결정.
// popup=true 는 전체완료(③) 강조용. 설계 §9.2 문구 그대로(반말 톤 유지).
// 스팟 보상(마이그143): res.spot_reward.granted 면 보상 문구를 신규완료·복구 지급 양쪽에 덧붙임.
export function buildD9Feedback(spotName, res) {
  const done = res && res.done != null ? res.done : null;
  const total = res && res.total != null ? res.total : null;
  const alreadyTagged = !!(res && res.already_tagged);
  const rewardMsg = formatSpotRewardMsg(res && res.spot_reward);

  if (!alreadyTagged) {
    const progress = done != null && total != null ? ` (${done}/${total})` : '';
    return { success: true, popup: false, message: `🌿 ${spotName} 퀘스트 완료!${progress}${rewardMsg}` };
  }
  // 🔴 이미 참여 → 전체화면 안내판(Figma 2937:39753)을 4초간 띄운다(2026-08-04 사용자 확정).
  //    적용 범위는 `already_tagged` **전부**(전진 완료 여부 무관) — 사용자 결정.
  //    ⚠️ 그래서 전체 미션을 다 끝낸 아이에게도 "다른 미션 스팟을 찾아" 로 안내된다(공시).
  //    `message` 는 남겨 둔다 — 화면은 고정 문구지만 로그·회귀 비교에 쓰이고, 안내판이
  //    렌더되지 않는 경로(예: 향후 축소 화면)에서 폴백으로 쓸 수 있다.
  //    `rewardMsg` 를 **따로 싣는다** — 재태깅 복구 지급(granted=true)이 일어날 수 있고(§4.5),
  //    고정 문구로 갈아치우면서 그 사실이 사라지면 아이가 보상받은 걸 모른다.
  const message = done != null && total != null && done === total
    ? `이미 참여했어~!${rewardMsg}`
    : `이미 참여했어, 다음 미션으로 고고~!${rewardMsg}`;
  return { success: true, popup: false, notice: NOTICE_ALREADY_DONE, rewardMsg, message };
}

/**
 * 이번 태깅이 **미션 3종(사진촬영·OX퀴즈·그림퀴즈) 완료** 경로인지 판정한다.
 *
 * 판정 근거는 `sendTag(qr, extra)` 의 `extra.mission_result` **키 존재**다 —
 * `handleMissionComplete` 만 그 키를 싣고, 일반 QR 태깅은 `extra` 자체가 없다.
 * 🔴 값이 아니라 **키 존재**로 본다: 오답이면 `false`·`null` 이 실릴 수 있는데
 *    값으로 판정하면 오답 미션이 일반 태깅으로 오분류된다.
 */
export function isMissionCompletionTag(extra) {
  return !!extra && Object.prototype.hasOwnProperty.call(extra, 'mission_result');
}

/**
 * 초록 완료 오버레이(`qt-overlay-success`)를 띄울지 판정한다 — 2026-08-28 사용자 요청.
 *
 * 미션 3종을 끝낸 직후에는 띄우지 않는다. 미션 화면이 이미 결과를 보여줬으므로
 * 같은 사실을 전면에 한 번 더 노출할 이유가 없다는 사용자 판단이다.
 *
 * 🔴 **일반 QR 태깅(미션 없는 스팟)은 그대로 둔다** — 같은 `sendTag` 를 타지만
 *    아이가 성공을 확인할 다른 화면이 없다.
 * 🔴 **안내판(`notice`)은 예외로 살린다** — "이미 참여했어" 처럼 진행되지 않은
 *    이유는 알려야 하고, 안내판은 초록 오버레이가 아니라 별도 전체화면이다
 *    (JSX 조건이 `feedback && !feedback.notice` 라 서로 다른 경로다).
 */
export function shouldShowTagFeedback(extra, fb) {
  if (!fb) return false;
  if (!isMissionCompletionTag(extra)) return true;
  return !!fb.notice;
}

/**
 * 완료 효과음을 재생할지 판정한다 — 오버레이와 **같은 범위**로 끈다(사용자 확정).
 *
 * ⚠️ 반려음(`shouldPlayMissionRejectedSound`)은 끄지 않는다. 왜 진행되지 않았는지는
 *    아이에게 알려야 하며, 그 판정은 호출부에 그대로 남아 있다.
 */
export function shouldPlayCompleteSoundForTag(extra, res) {
  return shouldPlayMissionCompleteSound(res) && !isMissionCompletionTag(extra);
}

/**
 * 🔴 "등록되지 않은 주렁밴드" 안내판을 띄울 `error_code` 집합 (Figma 2936:39627).
 *
 * 앱 등록으로 해결되는 것만 넣는다.
 *  · `BAND_UNASSIGNED` — 진짜 밴드인데 회원 미연결 (디자인 문구와 정확히 일치)
 *  · `UNKNOWN_BAND`    — 서명은 맞으나 DB 미등록(위조 포함)
 *  · `INVALID_BAND`    — 형식 오류
 *
 * ⚠️ 넣지 않는 것:
 *  · `BAND_REVOKED`   — 회수된 밴드. 앱 등록으로 해결되지 않고 **새 밴드**가 필요하다.
 *  · `BUSINESS_ERROR` — 서버가 여러 사유를 이 코드로 뭉친다("쿠폰 사용 미션은 태블릿 태깅
 *                       대상이 아닙니다" 등). 뭉쳐 매핑하면 미션타입 거부에 "밴드가 등록되지
 *                       않았다" 라는 **오안내**가 나간다.
 */
const UNREGISTERED_ERROR_CODES = Object.freeze([
  'BAND_UNASSIGNED', 'UNKNOWN_BAND', 'INVALID_BAND',
]);

/**
 * 🔴 "유효하지 않은 QR" 안내판을 띄울 `error_code` 집합 (Figma 2937:39751).
 *
 * 위 미등록 집합과 **정반대 안내**다 — 이쪽은 QR 이 죽어서 **새 밴드가 필요한** 경우다.
 *  · `BAND_REVOKED`     — 회수된 밴드. **사유 무관**(사용자 확정 2026-08-04): `revoked` 는
 *                         종착 상태라 분실·탈퇴·자녀삭제·배치폐기 전부 결론이 같다.
 *  · `MEMBER_WITHDRAWN` — 탈퇴 회원의 회원 QR. 탈퇴가 `qr_code` 를 덮어써 조회가 실패하므로
 *                         서버가 member_id 로 역판정해 이 코드를 내려준다.
 *
 * ⚠️ `BUSINESS_ERROR` 는 여기에도 넣지 않는다 — 서버가 여러 사유를 뭉치는 코드라
 *    미션타입 거부에 "QR이 유효하지 않다" 는 오안내가 나간다.
 */
const INVALID_QR_ERROR_CODES = Object.freeze([
  'BAND_REVOKED', 'MEMBER_WITHDRAWN',
]);

/**
 * 🔴 여권 QR 실패 코드 → 안내판 (2026-09-01, 설계 §4.8 (2)).
 *
 * **위 두 집합에 섞지 않는다.** 섞으면 여권 고객에게 "앱에서 **밴드**를 등록하면"이라는
 * 해결되지 않는 지시가 나간다 — 그 오안내가 이 기능의 출발점이다(§1.1).
 *
 * 🔴 안내판과 서버 `message` 는 **배타 렌더**다(`:1527` message 오버레이 vs `:1733` 안내판).
 *    안내판이 뜨면 서버 message 는 화면에 나오지 않으므로, 안내판을 뭉치면 화면에서 원인이
 *    사라진다. 그래서 만료·취소·미등록을 각각 따로 둔다.
 *
 * 뒤 4코드가 한 안내판을 공유하는 것은 **현장 대처가 완전히 같기 때문**이다(재스캔 후에도
 * 안 되면 매장 문의). 원인 구분이 필요한 운영자는 `passport_qr_usages`·서버 로그에서
 * error_code 로 확인한다(§4.1 원칙 4 의 단서).
 */
const PASSPORT_NOTICE_BY_CODE = Object.freeze({
  PASSPORT_QR_UNREGISTERED: NOTICE_PASSPORT_UNREGISTERED,  // 원장 available — 앱 등록 전
  PASSPORT_EXPIRED: NOTICE_PASSPORT_EXPIRED,               // D-1
  PASSPORT_CANCELLED: NOTICE_PASSPORT_CANCELLED,           // D-1
  PASSPORT_QR_VOID: NOTICE_PASSPORT_UNUSABLE,              // 원장 void(배치 폐기·인쇄불량)
  PASSPORT_QR_NOT_FOUND: NOTICE_PASSPORT_UNUSABLE,         // 원장 미등재 — 위조·오출력
  INVALID_PASSPORT_QR: NOTICE_PASSPORT_UNUSABLE,           // 형식·서명 오류
  PASSPORT_NOT_FOUND: NOTICE_PASSPORT_UNUSABLE,            // 댕글링(M4) — 텔레그램도 함께 나간다
});

/** 스캔 실패(서버 응답) → 피드백. 대상 코드면 안내판, 그 밖은 기존 메시지 오버레이. */
export function buildScanErrorFeedback(err) {
  const code = err && typeof err.error_code === 'string' ? err.error_code : '';
  const message = (err && err.message) || '태깅에 실패했어요. 다시 시도해 주세요.';
  if (UNREGISTERED_ERROR_CODES.includes(code)) {
    return { success: false, popup: false, notice: NOTICE_UNREGISTERED, message };
  }
  if (INVALID_QR_ERROR_CODES.includes(code)) {
    return { success: false, popup: false, notice: NOTICE_INVALID_QR, message };
  }
  // 회원 QR 자체가 무효(서명 불일치·미가입) — 밴드가 아니라 **가입** 문제라 안내가 다르다.
  // 서버가 2026-08-19 부터 BUSINESS_ERROR 대신 전용 코드를 준다(그 전에는 이 경우만
  // 안내판 없이 평문으로 떨어져 화면이 갈렸다).
  if (code === 'INVALID_MEMBER_QR') {
    return { success: false, popup: false, notice: NOTICE_INVALID_MEMBER_QR, message };
  }
  // 첫 미션 선행 게이트(설계 2026-08-04) — 서버가 tag·check 양쪽에서 같은 코드로 막는다.
  if (code === 'QUEST_FIRST_MISSION_REQUIRED') {
    return { success: false, popup: false, notice: NOTICE_FIRST_MISSION_FIRST, message };
  }
  // 🔴 여권 QR (2026-09-01) — **기존 분기 뒤**에 둔다. 앞에 두면 코드 집합이 겹치지 않아도
  //    "판정 순서가 바뀌었다"는 사실 자체가 회귀 위험이 된다. 뒤에 두면 기존 판정은 구조적으로
  //    불변이고, 여권 코드는 위 어느 집합에도 없으므로 여기까지 온다.
  if (PASSPORT_NOTICE_BY_CODE[code]) {
    return { success: false, popup: false, notice: PASSPORT_NOTICE_BY_CODE[code], message };
  }
  return { success: false, popup: false, message };
}

/**
 * 파싱 실패(`extractQuestScanCode` 가 null) → **전용** 안내판.
 *
 * 🔴 지금까지 이 경로는 **아무 화면도 띄우지 않았다** — 아이가 엉뚱한 QR(타사 QR·상품 바코드·
 *    구형 `JRB_` 밴드)을 비추면 태블릿이 무반응이라 고장으로 오해했다.
 *
 * 🔴 2026-09-01 **계약 변경**(설계 §4.8 (4)) — 이전에는 `NOTICE_UNREGISTERED`("등록되지 않은
 *    **주렁밴드**예요")를 재사용했다. 그런데 여권 QR 도 파서가 거르지 못해 이 경로로 떨어졌고,
 *    여권 고객이 밴드를 등록하라는 **해결되지 않는 지시**를 받았다. 이제 여권은 서버까지
 *    도달하므로(S10) 이 안내판은 **정말로 못 읽은 경우** 전용이 되고, 문구도 "무엇을 비춰야
 *    하는지"를 말한다.
 */
export function buildUnparsableFeedback() {
  return {
    success: false,
    popup: false,
    notice: NOTICE_UNPARSABLE,
    message: 'QR을 읽지 못했습니다',
  };
}

/**
 * 피드백 노출 시간(ms). 전체화면 안내판만 4초이고 나머지는 기존 값을 유지한다.
 * 한 곳에서 정해 두어야 노출 시간 변경이 코드 한 군데로 검증된다.
 */
export function feedbackDisplayMs(fb) {
  if (!fb || !fb.notice) return SUCCESS_DISPLAY_MS;
  // 안내판이 자기 시간을 들고 있으면 그것을 쓴다(예: 첫 미션 안내판 5초 — 사용자 지정).
  // 상수를 하나 더 만들어 분기하면 표시시간 판정이 둘로 갈라진다 — 여기가 단일 choke point.
  return typeof fb.notice.displayMs === 'number' ? fb.notice.displayMs : NOTICE_DISPLAY_MS;
}

/* 🔴 사진찍기 안내판 유틸(splitGuideLines·CAM_CONTENT_BUDGET_U·fitScale·buildCameraGuideLines)은
   2026-08-14 개정(Figma 3563:43238)으로 **전부 폐기**했다. 개정 화면은 나무 안내판 대신
   타이틀 이미지를 쓰므로 스팟 설명을 줄로 쪼개거나 간판 넘침을 계산할 일이 없다.
   ⚠️ 그 결과 이 화면에서 스팟명과 미션 설명이 사라진다(의도된 손실 — CameraScanBoard 주석 참조). */


// 효과음은 API가 명시적으로 신규 태깅을 확인한 경우에만 재생한다.
// 응답 누락/형식 변경도 무음 처리해 이미 완료한 미션에서 잘못 울리는 것을 방지한다.
export function shouldPlayMissionCompleteSound(res) {
  return res?.already_tagged === false;
}

export function shouldPlayMissionRejectedSound(res) {
  return res?.already_tagged === true;
}

/**
 * 이 스팟이 첫 번째 미션인가 — **서버 `is_first` 단독 판정** (기획 §4.4).
 *
 * 🔴 클라이언트가 `spots[0]` 로 직접 판정하던 구 헬퍼를 대체한다(옛 이름은 DoD grep 대상이라
 *    주석에도 쓰지 않는다).
 *    판정이 두 곳에 있으면 CRM 이 영상 업로드를 강제한 스팟과 태블릿이 실제로 재생하는 스팟이
 *    갈린다. 정의가 tools `spots[0]` / 앱 `sort_order=1` / 기획 "최솟값" **3종으로 갈렸던**
 *    것이 이 작업의 출발점이므로 4번째 사본을 만들지 않는다.
 *
 * 🔴 필드가 없으면(구버전 서버 응답·조회 실패) **false** 다. 클라 판정으로 되돌아가면 판정
 *    이원화가 그대로 되살아난다. false 로 떨어져도 화면은 비지 않는다 — 인트로 없이 기존
 *    완료 문구 경로를 타기 때문이다.
 *
 * `=== true` 로 엄격 비교한다. 서버는 JSON bool 을 보내므로 `"true"`·`1` 같은 값은 계약 위반
 * 이며, truthy 로 받아 주면 계약이 조용히 넓어진다.
 */
export function isFirstMissionSpot(spot) {
  return !!spot && spot.is_first === true;
}

/**
 * 재생할 인트로 영상 URL — **tag 응답이 권위, 키가 없으면 setup 값** (기획 §4.4 안 C).
 *
 * 서버 계약(`quest_service._tag_intro_fields` docstring):
 *   · tag 응답에 키 **있음** → 그 값이 권위. `null` 은 "영상 없음"(→ 완료 문구 경로).
 *   · 키 **없음**(서버측 조회 실패) → setup 에서 받은 스팟 값을 그대로 쓴다.
 *
 * 🔴 `res.intro_video_url || spot.intro_video_url` 로 짜면 **안 된다** — 서버가 의도적으로
 *    내려보낸 `null`(영상이 제거됨)이 setup 캐시의 옛 URL 로 되살아난다. 태블릿은 며칠씩
 *    켜져 있어 setup 캐시가 오래된 값을 들고 있다.
 *
 * 빈 문자열도 `null` 로 접는다. 서버가 sentinel(`OFF`)·빈 값을 정규화해 보내지만, 여기서도
 * 접어야 `<video src="">` 가 현재 페이지를 재요청하는 사고를 원천 차단한다.
 */
export function resolveIntroVideoUrl(tagRes, spot) {
  const hasTagKey = !!tagRes && typeof tagRes === 'object'
    && Object.prototype.hasOwnProperty.call(tagRes, 'intro_video_url');
  const url = hasTagKey ? tagRes.intro_video_url : (spot ? spot.intro_video_url : null);
  return typeof url === 'string' && url.trim() !== '' ? url : null;
}

/**
 * 재생할 **퀘스트 동선 영상** URL (마이그168a) — `resolveIntroVideoUrl` 과 완전히 같은 규약.
 *
 * 서버 계약: tag 응답에 키 **있음** → 그 값이 권위(`null` = 영상 없음).
 *            키 **없음**(조회 실패) → setup 에서 받은 스팟 값을 쓴다.
 *
 * 🔴 인트로와 마찬가지로 `res.x || spot.x` 로 짜면 안 된다 — 서버가 내려보낸 `null`(제거됨)이
 *    오래된 setup 캐시 값으로 되살아난다.
 * 🔴 동선 영상은 **선택 항목**이라 null 이 정상이다. 없으면 태블릿이 이 단계를 건너뛴다.
 */
export function resolveRouteVideoUrl(tagRes, spot) {
  const hasTagKey = !!tagRes && typeof tagRes === 'object'
    && Object.prototype.hasOwnProperty.call(tagRes, 'route_video_url');
  const url = hasTagKey ? tagRes.route_video_url : (spot ? spot.route_video_url : null);
  return typeof url === 'string' && url.trim() !== '' ? url : null;
}

/** 첫 미션 즉시 태그 우회는 수행형 미션에 적용하지 않는다(미션을 반드시 수행). */
export function shouldUseFirstMissionTagShortcut(spot) {
  return isFirstMissionSpot(spot)
    && !['ox_quiz', 'picture_quiz', 'funny_photo'].includes(spot.mission_type);
}

/** 첫 번째 미션의 검은 대기 화면에는 미션명·QR 아이콘·안내 문구를 렌더하지 않는다. */
/**
 * 인트로 영상 → 이름 인사 → 3분기 연출을 태울 스팟인가 (2026-08-19 시퀀스 개편 S1).
 *
 * 🔴 `shouldUseFirstMissionTagShortcut` 과 **반환값이 같아도 별개 함수로 둔다.**
 *    그 함수는 `sendTag` 의 **러너 우회 판정**(수행형은 미션을 반드시 수행)을 겸한다.
 *    "수행형도 인사를 보여주자"며 그쪽 제외 목록을 풀면 **OX·사진 첫 미션이 퀴즈를 풀지 않고
 *    즉시 완료 처리된다.** 의미가 다른 두 판정을 한 함수가 겸하지 않게 여기서 분리한다.
 *
 * 🔴 재태그(`already_tagged`) 여부를 **보지 않는다.** 새 사양은 매 태깅마다 같은 연출을 태우고
 *    (2026-08-05 의 "재태그는 지도만" 결정을 사용자 재확인으로 대체), 상황 구분은 인사 뒤의
 *    3분기가 맡는다. 여기서 재태그를 걸러내면 분기 2·3 이 다시 도달 불가가 된다.
 */
export function shouldRunFirstMissionCinematic(spot) {
  return isFirstMissionSpot(spot)
    && !['ox_quiz', 'picture_quiz', 'funny_photo'].includes(spot.mission_type);
}

export function shouldShowQrScanPrompt(firstMissionSelected) {
  return !firstMissionSelected;
}

/**
 * 첫 미션 대기 화면의 `BASE CAMP` 로고 표시 여부 (Figma `3379:44765`, 2026-08-14 취득).
 *
 * 🔴 **`shouldShowQrScanPrompt` 를 넓히지 않고 별도 판정을 둔다.** 그 함수는 "QR 안내
 *    3요소(미션명·아이콘·문구)를 보여주는가" 라는 뜻이고 첫 미션은 여전히 false 다.
 *    의미를 "무언가를 보여주는가" 로 바꾸면 그 함수의 기존 가드 2건이 뜻을 잃는다.
 *
 * 두 판정은 **서로 배타적**이다 — 같은 자리를 두 요소가 다투지 않는다(가드로 고정).
 */
export function shouldShowFirstMissionLogo(firstMissionSelected) {
  return !!firstMissionSelected;
}

/**
 * 인사 뒤 3분기의 표시 시간 — 세 분기가 **같은 값**을 쓴다(사용자 확정 2026-08-19).
 * 옛 상태 지도의 8초는 폐기됐다. 아이가 "곧 QR 대기로 돌아온다"를 학습하려면 일정해야 한다.
 */
export const FIRST_MISSION_OUTCOME_MS = 5000;

/**
 * 이름 인사 뒤 무엇을 보여줄지 — 3분기 판정.
 *
 *   ① `total > 0 && done >= total`  → `'complete'`      전부 완료
 *   ② `!alreadyTagged` + 동선영상 있음 → `'route'`        입장 후 첫 태그
 *      `!alreadyTagged` + 동선영상 없음 → `'map-start'`   지도 · "시작해볼까?"
 *   ③ 그 외(= 재태그)                 → `'map-progress'` 지도 · "모두 완료하세요!"
 *
 * 🔴 **2026-08-20 정정 — 구분자는 `done` 이 아니라 `alreadyTagged` 다.**
 *    이전 판정은 `done <= 1` 을 봤는데, 첫 미션을 태그하면 `done` 이 1 이 되고 같은 회원이
 *    **다시 태그해도 `done` 은 여전히 1** 이다(멱등 — 서버가 `INSERT IGNORE`).
 *    그래서 재태그가 영원히 "첫 참여"로 판정돼 **동선 영상이 계속 다시 재생**됐다(현장 버그).
 *    사용자 조건의 실제 구분자는 "이 스팟을 이미 태그했는가" 이고, 서버가 `/tag` 응답에
 *    `already_tagged` 로 이미 주고 있었다.
 *
 * 🔴 **①을 먼저 본다.** 첫 미션이 유일한 스팟인 지점에서는 첫 태그로도 전부 완료가 된다.
 *    그 아이는 실제로 다 끝낸 것이므로 완료 화면이 맞다.
 *
 * 🔴 `total` 이 0·null 이면 **완료로 오판하지 않는다.** 세트 이상인데 "다 했어요"를 띄우면
 *    아이가 미션을 시작조차 못 한다. 판단이 안 서면 지도로 떨어뜨린다.
 *
 * 참고: "첫 태그인데 done 이 이미 여럿" 인 상태는 생기지 않는다 — 베이스캠프를 하기 전에는
 * 서버가 다른 미션을 막는다(`QUEST_FIRST_MISSION_REQUIRED`).
 */
export function resolveFirstMissionOutcome(input) {
  const { alreadyTagged, done, total, routeVideoUrl } = input || {};
  const doneN = Number.isFinite(Number(done)) ? Number(done) : 0;
  const totalN = Number.isFinite(Number(total)) ? Number(total) : 0;

  if (totalN > 0 && doneN >= totalN) return 'complete';
  if (!alreadyTagged) {
    const url = typeof routeVideoUrl === 'string' ? routeVideoUrl.trim() : '';
    return url !== '' ? 'route' : 'map-start';
  }
  return 'map-progress';
}

/** 분기 결과 → `firstMissionIntro.phase`. 모르는 값은 지도로 떨어뜨린다(검은 화면 금지). */
export function firstMissionPhaseFor(outcome) {
  if (outcome === 'route') return 'route';
  if (outcome === 'complete') return 'complete';
  return 'map';
}

/** 지도 배너 2행 문구 — 두 지도 분기를 **문구로만** 구분한다(Figma 3379:44594 / 3379:44803). */
export function firstMissionBannerTextFor(outcome) {
  return outcome === 'map-progress' ? '미션을 모두 완료하세요!' : '오늘의 미션을 시작해볼까?';
}

/** 서버가 별명·이름 부재 시 보내는 중립 표시명 (`tools_quest.py` `_member_display_name`). */
const SERVER_NEUTRAL_NAME = '친구';

/** 인사 1행 — 이름과 무관한 고정 문구 (Figma 3955:51667, 2026-08-20 개편). */
export const FIRST_MISSION_GREETING_HELLO = '어서와!';

/** 인사 본문 2행 — 고정 문구 (Figma 3955:51667). */
export const FIRST_MISSION_GREETING_LINE2 = '주렁미션에 입장하셨습니다!';

/**
 * 인사 본문 1행 조립 — `용감한 모험가 {이름}님이` (Figma 3955:51667).
 *
 * 🔴 **`buildFirstMissionAgentTitle` 을 고쳐 재사용하지 않는다.** 그 함수는 주키퍼 지도
 *    배너(`MissionStatusMap` 의 `agentTitle`)가 쓰고 있어, 함께 바꾸면 사용자가 요청하지
 *    않은 화면까지 조용히 바뀐다. 인사 화면 전용 조립기를 따로 둔다.
 *
 * 🔴 서버는 별명·이름이 둘 다 없으면 중립값 `'친구'` 를 보낸다(`SERVER_NEUTRAL_NAME`).
 *    그대로 끼우면 `용감한 모험가 친구님이` 가 된다 — 어색하다. 이름 자리를 비우고
 *    앞 조각의 공백도 함께 지워 **`용감한 모험가님이`** 로 읽히게 한다.
 *
 * @returns {{prefix: string, name: string, suffix: string}} 이름이 없으면 `name` 이 빈 문자열.
 */
export function buildFirstMissionEntryLine(memberNickname) {
  const nickname = typeof memberNickname === 'string' ? memberNickname.trim() : '';
  const name = nickname && nickname !== SERVER_NEUTRAL_NAME ? nickname : '';
  return { prefix: name ? '용감한 모험가 ' : '용감한 모험가', name, suffix: '님이' };
}

/**
 * 인사 2행 — `견습 주키퍼 {이름} 요원` (Figma 3698:70357, 2026-08-19 교체).
 * 이전: `{별명}, 이제 네 차례야!` 한 줄.
 *
 * 🔴 서버는 별명·이름이 둘 다 없으면 **중립값 `'친구'`** 를 보낸다. 빈 문자열이 아니라서
 *    "값 부재" 판정으로는 걸러지지 않고, 그대로 끼우면 화면에 **`견습 주키퍼 친구 요원`**
 *    이 뜬다. 그래서 **화면이 중립값을 알아보고 이름 자리를 비운다.**
 *    서버 계약은 바꾸지 않는다 — 그 값은 OX 인사말 등 다른 화면도 쓴다.
 * 🔴 이름이 비면 공백이 두 번 들어가지 않도록 조립한다.
 */
export function buildFirstMissionAgentTitle(memberNickname) {
  const nickname = typeof memberNickname === 'string' ? memberNickname.trim() : '';
  const named = nickname && nickname !== SERVER_NEUTRAL_NAME ? `${nickname} ` : '';
  return `견습 주키퍼 ${named}요원`;
}

/**
 * 첫 번째 콤보 미션 전용: 인트로 영상(**끝까지**) → 3초 검은 화면 인사.
 *
 * 영상 URL 은 더 이상 모듈 상수가 아니라 **`videoUrl` prop** 이다(지점별로 다르다).
 * 재생 구간에는 워치독 2종이 붙어, 어떤 이유로 멈추더라도 반드시 인사 단계로 빠져나간다.
 */
/** CRM에서 지정한 미션별 대기화면. URL 누락·로드 실패를 숨기지 않고 오류로 표시한다. */
export function MissionWaitingScreen({ imageUrl, alt }) {
  const [loadFailed, setLoadFailed] = useState(false);
  const normalizedUrl = typeof imageUrl === 'string' ? imageUrl.trim() : '';

  useEffect(() => {
    setLoadFailed(false);
  }, [normalizedUrl]);

  if (!normalizedUrl || loadFailed) {
    return (
      <div className="qt-waiting-screen-error" data-testid="quest-waiting-screen-error">
        미션 대기화면을 불러올 수 없습니다.
      </div>
    );
  }
  return (
    <img
      className="qt-custom-waiting-screen"
      src={normalizedUrl}
      alt={alt || '미션 대기화면'}
      onError={() => setLoadFailed(true)}
    />
  );
}

export function FirstMissionIntro({
  phase, memberNickname, videoUrl, onVideoFinished, onVideoAbort,
  routeVideoUrl = null, onRouteFinished, onRouteAbort,
  ariaLabel = '모험 시작 영상', routeAriaLabel = '퀘스트 동선 영상',
}) {
  // 워치독 2종(로딩·무진행)은 **훅이 소유**한다 — OX 수행 흐름 3화면과 같은 재생 계약이라
  // 2026-08-12 에 `hooks/useQuestVideoWatchdog.js` 로 추출했다. 동작은 그대로다.
  //
  // 🔴 훅을 **두 번** 부른다(인트로 1개 + 동선 1개, 마이그168a). 하나를 phase 에 따라
  //    URL 만 갈아끼우는 방식은 쓰지 않는다 — 훅 내부의 `settledRef`·워치독 타이머가
  //    영상 사이에서 초기화되지 않아, 인트로에서 이탈 처리된 상태가 동선 재생에 그대로
  //    이어져 **동선 영상이 시작하자마자 종료 처리**된다.
  //    훅 호출은 조건부가 아니므로(항상 2회) Rules of Hooks 를 위반하지 않는다.
  const intro = useQuestVideoWatchdog({
    active: phase === 'video',
    videoUrl,
    onFinished: onVideoFinished,
    onAbort: onVideoAbort,
    logLabel: '인트로 영상',
  });
  const route = useQuestVideoWatchdog({
    active: phase === 'route',
    videoUrl: routeVideoUrl,
    onFinished: onRouteFinished,
    onAbort: onRouteAbort,
    logLabel: '동선 영상',
  });

  if (intro.isVideoPhase) {
    return (
      <div className="qt-first-mission-intro" data-testid="quest-first-mission-video">
        <video
          ref={intro.videoRef}
          className={`qt-first-mission-video${intro.videoPlaying ? ' qt-first-mission-video-playing' : ''}`}
          src={videoUrl}
          autoPlay
          playsInline
          preload="auto"
          aria-label={ariaLabel}
          {...intro.videoHandlers}
        />
      </div>
    );
  }
  if (route.isVideoPhase) {
    // 인트로와 **같은 클래스**를 쓴다(전체화면 검은 배경 + 페이드인). 두 영상이 연달아
    // 재생되므로 레이아웃이 달라지면 전환 순간에 화면이 튄다.
    return (
      <div className="qt-first-mission-intro" data-testid="quest-first-mission-route-video">
        <video
          ref={route.videoRef}
          className={`qt-first-mission-video${route.videoPlaying ? ' qt-first-mission-video-playing' : ''}`}
          src={routeVideoUrl}
          autoPlay
          playsInline
          preload="auto"
          aria-label={routeAriaLabel}
          {...route.videoHandlers}
        />
      </div>
    );
  }
  if (phase === 'greeting') {
    // Figma 3955:51667 — 절대좌표 5요소. 좌표 근거는 QuestTagPage.css 주석과
    // docs/2026-08-20-first-mission-greeting-redesign-dev-steps.md §1.3 참조.
    const entry = buildFirstMissionEntryLine(memberNickname);
    return (
      <div className="qt-first-mission-intro" data-testid="quest-first-mission-greeting">
        <div className="qt-first-mission-greeting" aria-live="polite">
          <span className="qt-first-mission-greeting-leaf-left" aria-hidden="true" />
          <span className="qt-first-mission-greeting-leaf-right" aria-hidden="true" />
          <p className="qt-first-mission-greeting-hello">{FIRST_MISSION_GREETING_HELLO}</p>
          <p className="qt-first-mission-greeting-line1">
            {entry.prefix}
            {entry.name && (
              <em className="qt-first-mission-greeting-name">{entry.name}</em>
            )}
            {entry.suffix}
          </p>
          <p className="qt-first-mission-greeting-line2">{FIRST_MISSION_GREETING_LINE2}</p>
          <span
            className="qt-first-mission-greeting-mark"
            role="img"
            aria-label="주 캠프"
          />
        </div>
      </div>
    );
  }
  return null;
}

export default function QuestTagPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true); // 셋업 로딩
  const [setupError, setSetupError] = useState(null); // 셋업 조회 실패
  const [activeSet, setActiveSet] = useState(null); // {id,name,reward_coupon_name}|null
  const [spots, setSpots] = useState([]); // active 스팟 목록
  const [selectedSpot, setSelectedSpot] = useState(null); // {id,name,mission_type,mission_config,mission_config_version,...}

  const [phase, setPhase] = useState(PHASE_SCAN); // scan | mission (ox/funny 수행 중)
  const [photoCamera, setPhotoCamera] = useState(readPhotoCamera);
  const [cameraStorageWarning, setCameraStorageWarning] = useState(false);
  const changePhotoCamera = useCallback((value) => {
    setPhotoCamera(value);
    setCameraStorageWarning(!savePhotoCamera(value));
  }, []);
  const [pendingQr, setPendingQr] = useState(null); // 미션 수행 중인 회원 QR(식별값)
  // OX 인사말 화면(②)이 `{별명}요원` 으로 부른다. `/tools/quest/check` 가 내려준다(계약 C1).
  // 🔴 **영속화하지 않는다** — 다음 회원이 앞 사람의 별명으로 불리면 안 된다.
  const [pendingNickname, setPendingNickname] = useState(null);
  const [busy, setBusy] = useState(false); // tag/check 통신 중(스캐너 잠금)
  const [feedback, setFeedback] = useState(null); // { success, message, popup }
  // {phase, memberNickname, videoUrl, routeVideoUrl, spotId}|null
  //   phase: 'video'(인트로) → 'route'(퀘스트 동선, 있을 때만) → 'greeting'(별명 인사)
  //   videoUrl/routeVideoUrl/spotId 는 이탈 보고에도 쓰인다.
  const [firstMissionIntro, setFirstMissionIntro] = useState(null);
  // 첫 미션 재태그 시 띄우는 미션 상태 지도(8초). 안내판과 같은 규약 — 표시 중 스캔 무시.
  const cooldownMap = useRef(new Map());
  const feedbackTimerRef = useRef(null);
  // 🔴 영상 20초 상한 타이머(firstMissionVideoTimerRef)는 제거됐다 — 상한 대신 워치독 2종이
  //    FirstMissionIntro 안에서 이탈을 감지한다. 여기 남은 타이머는 인사 3초 하나뿐이다.
  const firstMissionGreetingTimerRef = useRef(null);
  // 분기 화면(지도·완료) 표시 타이머. 🔴 인사 타이머와 **같은 정리 함수**에 합류시킨다 —
  // 따로 두면 언마운트 경로가 갈라져 누수가 생긴다.
  const firstMissionOutcomeTimerRef = useRef(null);
  // 인사 동안 숨길 지도 데이터(prefetch 결과).
  const [firstMissionMap, setFirstMissionMap] = useState(null);
  const missionCompleteSoundRef = useRef(null);
  const missionRejectedSoundRef = useRef(null);

  const clearFirstMissionIntroTimers = useCallback(() => {
    if (firstMissionOutcomeTimerRef.current) {
      clearTimeout(firstMissionOutcomeTimerRef.current);
      firstMissionOutcomeTimerRef.current = null;
    }
    if (firstMissionGreetingTimerRef.current) {
      clearTimeout(firstMissionGreetingTimerRef.current);
      firstMissionGreetingTimerRef.current = null;
    }
  }, []);

  /** 무음 재조회 게이트 — 렌더마다 갱신하고 인터벌은 이 값을 읽기만 한다(아래 effect 참조). */
  const setupRefreshIdleRef = useRef(false);

  // ── 셋업 조회 + 저장된 스팟 복원 ──────────────────────────
  const loadSetup = useCallback(async () => {
    setLoading(true);
    setSetupError(null);
    try {
      const data = (await getQuestSetup()) || {};
      const set = data.active_set || null;
      const spotList = Array.isArray(data.spots) ? data.spots : [];
      setActiveSet(set);
      setSpots(spotList);

      // 저장된 스팟이 현재 active 스팟 목록에 여전히 존재하면 복원
      if (set && spotList.length > 0) {
        const savedId = Number(localStorage.getItem(spotStorageKey()));
        const found = spotList.find((s) => s.id === savedId);
        setSelectedSpot(found || null);
        persistSpotName(found ? found.name : null); // 복원/탈락 시 라벨 이름 동기화(스테일 제거)
      } else {
        setSelectedSpot(null);
        persistSpotName(null);
      }
    } catch (err) {
      setSetupError(err?.message || '퀘스트 정보를 불러오지 못했습니다');
      setActiveSet(null);
      setSpots([]);
      setSelectedSpot(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSetup();
  }, [loadSetup]);

  /**
   * 유휴 시 설정을 **조용히** 다시 읽는다 — CRM 변경(결과 영상·문항·스팟)을 재시작 없이 받는다.
   *
   * 🔴 `loadSetup` 을 그대로 주기 실행하면 안 된다. 그 함수는 `setLoading(true)` 로 로딩 화면을
   *    띄우고, 실패 시 `setSetupError` + `setSpots([])` 로 **화면을 파괴**한다. 태블릿은 일시적
   *    네트워크 흔들림이 잦아, 한 번 실패했다고 운영 중인 화면을 비우면 안 된다.
   *    → 여기서는 **성공했을 때만** 반영하고 실패는 조용히 넘긴다(다음 주기에 다시 시도).
   *
   * 🔴 선택 스팟은 `localStorage` 에 저장돼 있어(`:persistSpot`) 같은 규칙으로 복원한다 —
   *    운영자가 고른 미션이 재조회 때문에 초기화되지 않는다.
   */
  const refreshSetupQuietly = useCallback(async () => {
    try {
      const data = (await getQuestSetup()) || {};
      // 요청 당시 유휴였어도 응답 전 QR 스캔/미션이 시작될 수 있다.
      // 진행 중인 영상 URL·미션 버전을 늦은 응답으로 교체하지 않는다.
      if (!setupRefreshIdleRef.current) return;
      const set = data.active_set || null;
      const spotList = Array.isArray(data.spots) ? data.spots : [];
      setActiveSet(set);
      setSpots(spotList);
      if (set && spotList.length > 0) {
        const savedId = Number(localStorage.getItem(spotStorageKey()));
        const found = spotList.find((sp) => sp.id === savedId);
        setSelectedSpot(found || null);
        persistSpotName(found ? found.name : null);
      } else {
        setSelectedSpot(null);
        persistSpotName(null);
      }
    } catch (err) {
      // 조용히 넘긴다 — 다음 주기에 다시 시도한다. 화면 상태는 건드리지 않는다.
    }
  }, []);

  useEffect(() => {
    return () => {
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
      clearFirstMissionIntroTimers();
    };
  }, [clearFirstMissionIntroTimers]);

  useEffect(() => {
    const completeAudio = new Audio(MISSION_COMPLETE_SOUND_SRC);
    const rejectedAudio = new Audio(MISSION_REJECTED_SOUND_SRC);
    completeAudio.preload = 'auto';
    rejectedAudio.preload = 'auto';
    missionCompleteSoundRef.current = completeAudio;
    missionRejectedSoundRef.current = rejectedAudio;

    return () => {
      completeAudio.pause();
      rejectedAudio.pause();
      missionCompleteSoundRef.current = null;
      missionRejectedSoundRef.current = null;
    };
  }, []);

  // 안내판 폰트(GosanjaOTF) 선행 로드는 **앱 진입점(`App.js`)** 이 담당한다 —
  // 이 화면에 도달하기 전(로그인·라우팅)부터 받아 두는 편이 빠르고, 두 곳에서 받으면
  // 어느 쪽이 실제로 동작하는지 흐려진다. `src/utils/preloadNoticeFont.js` 참조.

  const stopMissionSounds = useCallback(() => {
    [missionCompleteSoundRef.current, missionRejectedSoundRef.current].forEach((audio) => {
      if (!audio) return;
      audio.pause();
      audio.currentTime = 0;
    });
  }, []);

  const playMissionSound = useCallback((kind) => {
    const audio = kind === 'complete'
      ? missionCompleteSoundRef.current
      : missionRejectedSoundRef.current;
    if (!audio) return;

    stopMissionSounds();
    audio.currentTime = 0;
    const playPromise = audio.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch((err) => {
        // 효과음 실패는 미션 완료 처리 자체를 막지 않는다.
        console.warn(`[QuestTagPage] 미션 ${kind} 효과음을 재생하지 못했습니다.`, err);
      });
    }
  }, [stopMissionSounds]);

  // 미션 흐름 초기화(스캔 화면으로 복귀)
  const resetMissionFlow = useCallback(() => {
    setPhase(PHASE_SCAN);
    setPendingQr(null);
    setBusy(false);
  }, []);

  // 결과 피드백 표시 후 스캔 화면 복귀 예약
  const showFeedback = useCallback((fb) => {
    setFeedback(fb);
    if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    // 전체화면 안내판은 4초, 나머지는 기존 2.5초 — 판정은 feedbackDisplayMs 한 곳에서 한다.
    feedbackTimerRef.current = setTimeout(() => setFeedback(null), feedbackDisplayMs(fb));
  }, []);

  const showFirstMissionIntro = useCallback(
    (memberNickname, videoUrl, spotId, routeVideoUrl = null, counts = {}) => {
      clearFirstMissionIntroTimers();
      setFeedback(null);
      setFirstMissionIntro({
        phase: 'video', memberNickname, videoUrl, routeVideoUrl, spotId,
        // 인사 뒤 분기 판정의 입력. 태그 응답 시점 값을 그대로 들고 간다 —
        // 나중에 다시 조회하면 그 사이 다른 아이가 태깅해 값이 흔들릴 수 있다.
        done: counts.done, total: counts.total, alreadyTagged: counts.alreadyTagged,
      });
    }, [clearFirstMissionIntroTimers],
  );

  const showFirstMissionGreeting = useCallback(() => {
    // onEnded 와 워치독이 거의 동시에 와도 인사 타이머는 한 번만 만든다.
    if (firstMissionGreetingTimerRef.current) return;
    setFirstMissionIntro((current) => (
      current ? { ...current, phase: 'greeting' } : current
    ));
    firstMissionGreetingTimerRef.current = setTimeout(() => {
      firstMissionGreetingTimerRef.current = null;
      // 🔴 인사가 끝나면 **분기 판정**으로 넘어간다(2026-08-19). 예전처럼 여기서 곧장
      //    QR 대기로 돌아가면 R4~R9(3분기)가 통째로 실행되지 않는다.
      setFirstMissionIntro((cur) => {
        if (!cur) return cur;
        const outcome = resolveFirstMissionOutcome({
          // 🔴 재태그 판정의 **핵심 입력**. 빠뜨리면 `!alreadyTagged` 가 항상 참이 되어
          //    재태그에도 동선 영상이 다시 재생된다(2026-08-20 현장 버그).
          alreadyTagged: cur.alreadyTagged,
          done: cur.done, total: cur.total, routeVideoUrl: cur.routeVideoUrl,
        });
        return { ...cur, phase: firstMissionPhaseFor(outcome), outcome };
      });
    }, FIRST_MISSION_GREETING_MS);
  }, []);

  // 분기 화면(지도·완료)은 5초 뒤 스스로 닫혀 QR 대기로 돌아간다.
  // 🔴 세 분기가 **같은 상수**를 쓴다 — 아이가 "곧 돌아온다"를 학습하려면 일정해야 한다.
  //    타이머는 phase 가 분기로 바뀔 때 한 번만 만든다(중복 생성 시 조기 종료가 난다).
  const outcomePhase = firstMissionIntro?.phase;
  useEffect(() => {
    if (outcomePhase !== 'map' && outcomePhase !== 'complete') return undefined;
    if (firstMissionOutcomeTimerRef.current) return undefined;
    firstMissionOutcomeTimerRef.current = setTimeout(() => {
      firstMissionOutcomeTimerRef.current = null;
      setFirstMissionIntro(null);
      setFirstMissionMap(null);
    }, FIRST_MISSION_OUTCOME_MS);
    return undefined;
  }, [outcomePhase]);

  /**
   * 첫 미션 흐름 **종료** — QR 대기로 돌아간다.
   *
   * 🔴 동선 영상은 분기 1의 **종착지**다. 이전에는 `onRouteFinished` 가
   *    `showFirstMissionGreeting` 으로 배선돼 있었는데(옛 순서 영상→동선→인사의 잔재),
   *    2026-08-19 개편으로 동선이 인사 **뒤**로 오면서 그 배선이 닫힌 고리가 됐다:
   *      인사 → route → 동선 종료 → 인사 → route → …  (done/total 이 불변이라 영원히 반복)
   *    2026-08-20 현장에서 실제로 무한반복이 관측됐다.
   *
   * 🔴 워치독은 정상 종료와 이탈을 **같은 출구**로 내보내므로(`useQuestVideoWatchdog`),
   *    영상이 실패해도 여기로 와서 흐름이 끝난다 — 아이가 검은 화면에 갇히지 않는다.
   */
  const finishFirstMissionFlow = useCallback(() => {
    clearFirstMissionIntroTimers();
    setFirstMissionIntro(null);
    setFirstMissionMap(null);
  }, [clearFirstMissionIntroTimers]);

  // 🔴 인트로 종료의 **유일한 출구**. 2026-08-19 개편으로 **곧장 인사**로 간다.
  //    이전에는 여기서 동선 영상 유무를 보고 route 로 갈라졌는데, 새 사양은 동선을
  //    **인사 뒤 분기 판정**이 정한다(오늘 첫 참여일 때만 재생). 여기서 다시 갈라지면
  //    이미 미션을 여러 개 한 아이도 길 안내를 다시 본다.
  //    ⚠️ 워치독은 정상 종료와 이탈을 **같은 출구**로 내보낸다 — 인트로가 이탈해도 흐름은 잇는다.
  const handleFirstMissionIntroFinished = useCallback(() => {
    showFirstMissionGreeting();
  }, [showFirstMissionGreeting]);

  /**
   * 인트로 영상 이탈(로딩 워치독 만료 · 무진행 5초 · onError) → 서버 보고.
   *
   * 아이가 영상을 못 본 채 진행된 것이라 **현장 장애**다. 로그만 남기면 운영팀이 인지할 수
   * 없으므로 CLAUDE.md §10(silent failure 금지) 대상이다.
   * 보고는 fire-and-forget 이며(`reportQuestIntroVideoError` 가 예외를 흡수), 성공 여부와
   * 무관하게 FirstMissionIntro 가 이어서 인사 단계로 넘긴다.
   */
  const handleFirstMissionVideoAbort = useCallback((reason) => {
    reportQuestIntroVideoError({
      reason,
      spotId: firstMissionIntro ? firstMissionIntro.spotId : null,
      videoUrl: firstMissionIntro ? firstMissionIntro.videoUrl : null,
      videoKind: 'intro',
    });
  }, [firstMissionIntro]);

  // 동선 영상 이탈(마이그168a) — 인트로와 **별도 보고**다. `video_kind` 를 실어 보내지
  // 않으면 서버가 두 영상을 같은 알림 제목·같은 throttle 키로 묶어, 한쪽 장애가 다른 쪽에
  // 눌려 사라진다(운영팀이 엉뚱한 파일을 확인하게 된다).
  const handleFirstMissionRouteAbort = useCallback((reason) => {
    reportQuestIntroVideoError({
      reason,
      spotId: firstMissionIntro ? firstMissionIntro.spotId : null,
      videoUrl: firstMissionIntro ? firstMissionIntro.routeVideoUrl : null,
      videoKind: 'route',
    });
  }, [firstMissionIntro]);

  // 스팟 선택 → localStorage 영속
  const handleSelectSpot = useCallback((spot) => {
    clearFirstMissionIntroTimers();
    setSelectedSpot(spot);
    setPhase(PHASE_SCAN);
    setPendingQr(null);
    setFeedback(null);
    setFirstMissionIntro(null);
    try {
      localStorage.setItem(spotStorageKey(), String(spot.id));
    } catch {
      /* 저장 실패해도 메모리 상태로 동작 — 새로고침 시 재선택 */
    }
    persistSpotName(spot.name); // device-manager 라벨용 스팟 이름 동기화
  }, [clearFirstMissionIntroTimers]);

  // 🔴 잠금 판정과 사유를 **한 식**에서 만든다 (2026-08-07).
  //    예전에는 `canOpenSpotPicker` boolean 이라 "왜 막혔는지" 를 잃었고, 관리자 바의 미션선택은
  //    `opacity:0` 투명 select 라 disabled 가 붙어도 화면이 그대로였다 → 커서 없는 태블릿에서
  //    시각 신호가 0 이라 운영자가 "터치 고장" 으로 보고했다.
  //    판정을 boolean 과 사유 문자열로 나누면 둘이 갈라져 "막혔는데 이유가 틀린" 표시가 나온다.
  //    **null = 변경 가능**, 문자열 = 그 사유로 잠김.
  const spotPickLockReason =
    spots.length === 0 ? '미션 목록을 불러오지 못했습니다'
      : !selectedSpot ? '미션이 선택되지 않았습니다'
        : (busy || feedback || firstMissionIntro || phase !== PHASE_SCAN)
          ? '미션 진행 중에는 변경할 수 없습니다'
          : null;
  const firstMissionSelected = isFirstMissionSpot(selectedSpot);

  // 🔴 관리자 바(오른쪽 아래 5회 터치)가 떠 있는 동안은 스캔을 멈춘다 (2026-08-07).
  //    안 그러면 운영자가 조작 중인데 고객 태그가 들어와 미션이 **바 아래에서 보이지 않게**
  //    시작되고, 그 상태 변화가 위 `spotPickLockReason` 을 켜서 미션선택을 잠근다.
  //    바가 열린 태블릿은 취급 중이 아니므로 그동안 들어온 태그는 버린다.
  const [adminOpen, setAdminOpen] = useState(false);

  // 🔴 유휴 판정을 **새로 만들지 않는다** — 위 `spotPickLockReason` 이 단일 출처다
  //    (busy·feedback·firstMissionIntro·phase 를 한 식에서 본다). 판정을 두 벌로 나누면
  //    "막혔는데 이유가 틀린" 표시가 나온다는 경고가 그 주석에 이미 적혀 있다.
  //    `adminOpen` 은 그 식에 없으므로 여기서 함께 본다 — 운영자가 바를 열고 조작하는 중에
  //    스팟 목록이 발밑에서 바뀌면 선택이 튄다.
  setupRefreshIdleRef.current = spotPickLockReason === null && !adminOpen;

  /**
   * 유휴일 때만 도는 설정 재조회 인터벌.
   *
   * 🔴 `spotPickLockReason` 을 의존성에 넣지 않는다 — 렌더마다 인터벌이 재생성돼 주기가
   *    사실상 무의미해진다(매 렌더 0초부터 다시 셈). 유휴 여부는 **ref 로 읽는다.**
   * 🔴 미션 진행 중에는 돌지 않는다 — 실행 중인 미션의 `mission_config` 가 발밑에서 갈린다.
   */
  useEffect(() => {
    const timer = setInterval(() => {
      if (!setupRefreshIdleRef.current) return;
      refreshSetupQuietly();
    }, SETUP_REFRESH_MS);
    return () => clearInterval(timer);
  }, [refreshSetupQuietly]);

  // 🔴 사진찍기 안내판이 간판 밖으로 넘치지 않게 한다 (2026-08-07).
  //    디자인 수치는 짧은 예시 문구 기준이라 실제 스팟명·설명에서는 줄이 늘어난다(fitScale 주석).
  //    `transform: scale` 은 레이아웃 높이를 바꾸지 않으므로 재측정이 되먹임을 일으키지 않는다.

  // 🔴 구 스팟 피커 모달(+ 여는 핸들러)은 제거했다 — 유일한 진입점이던 헤더 트리거가 사라져
  //    도달 불가 코드가 됐다. 미션 변경은 이제 관리자 바의 콤보가 담당한다.
  //    최초 선택 경로는 그대로다 — 스팟 미선택 시 본문이 `qt-select-state` 선택 화면을 띄운다.

  // 같은 QR 쿨다운 확인
  const isCooldown = (qrCode) => {
    const last = cooldownMap.current.get(qrCode);
    return !!last && Date.now() - last < COOLDOWN_MS;
  };

  // 진행 중 tag 재진입 차단(더블탭·중복 onComplete → tag 1회만). ref=동기라 busy(state)보다 확실.
  const sendingRef = useRef(false);

  // ── tag 전송(공통) — extra 는 ox/funny 미션 결과(qr 은 null) ──
  const sendTag = useCallback(
    async (qrCode, extra) => {
      const spot = selectedSpot;
      if (!spot) return;
      if (sendingRef.current) return; // 이미 전송 중 → 중복 tag 차단(choke point)
      sendingRef.current = true;
      setBusy(true);
      try {
        const res = await tagQuestSpot(qrCode, spot.id, extra);
        // 첫 미션(QR형)은 재태그 여부와 무관하게 **항상** 인트로 → 이름 인사 → 3분기를 태운다
        // (2026-08-19 시퀀스 개편). 옛 `already_tagged` 조기 반환 + 8초 지도는 철거됐다 —
        // 분기 2("다른 미션 수행중")·분기 3("모두 완료")이 정의상 재태그라 그 반환이 살아 있으면
        // 두 요구가 코드에 도달조차 못 했다.
        const cinematic = shouldRunFirstMissionCinematic(spot);
        const introVideoUrl = cinematic ? resolveIntroVideoUrl(res, spot) : null;
        // 동선 영상(마이그168a)은 인트로가 있을 때만 유효하다 — 인트로 없이 단독 재생하지 않는다.
        const routeVideoUrl = introVideoUrl ? resolveRouteVideoUrl(res, spot) : null;
        if (cinematic) {
          // 🔴 인트로 영상이 없어도 **인사부터 시작**한다(사용자 확정 2026-08-19).
          //    D9 안내문은 생략한다 — `showFirstMissionIntro` 가 `setFeedback(null)` 을 부르므로
          //    안내문과 인사는 서로를 지워 동시 표시가 불가능하다. 대신 **효과음은 남긴다**:
          //    화면이 곧바로 인사로 바뀌어도 태그가 먹었는지를 소리로 알 수 있어야 한다.
          //    영상이 null 이면 `useQuestVideoWatchdog` 가 즉시 다음 단계로 넘겨 검은 화면이 없다.
          if (!introVideoUrl) {
            if (shouldPlayMissionCompleteSound(res)) {
              playMissionSound('complete');
            } else if (shouldPlayMissionRejectedSound(res)) {
              playMissionSound('rejected');
            }
          }
          // 🔴 지도를 **지금** 미리 받는다(인트로·인사 재생 동안 왕복을 숨긴다).
          //    분기 판정 뒤에 부르면 인사가 끝나고 배너만 뜬 채 기다리게 된다.
          //    실패해도 흐름을 막지 않는다 — 지도 없이 배너만 5초 띄우고 QR 대기로 간다.
          setFirstMissionMap(null);
          fetchQuestMissionMap(qrCode)
            .then((m) => setFirstMissionMap(canRenderStatusMap(m) ? m : null))
            .catch(() => setFirstMissionMap(null));
          showFirstMissionIntro(
            res?.member_nickname, introVideoUrl, spot.id, routeVideoUrl,
            { done: res?.done, total: res?.total, alreadyTagged: res?.already_tagged === true },
          );
        } else {
          // 미션 3종 완료 직후에는 초록 완료 오버레이·완료 효과음을 띄우지 않는다
          // (2026-08-28 사용자 요청). 판정은 순수 함수 두 개에 모아 두었다 — 화면과
          // 소리가 **같은 범위**로 꺼져야 하는데 조건이 두 곳으로 갈라지면 반드시 어긋난다.
          const fb = buildD9Feedback(spot.name, res);
          if (shouldShowTagFeedback(extra, fb)) {
            showFeedback(fb);
          }
          if (shouldPlayCompleteSoundForTag(extra, res)) {
            playMissionSound('complete');
          } else if (shouldPlayMissionRejectedSound(res)) {
            // 반려음은 미션 여부와 무관하게 유지 — 진행되지 않은 이유를 알려야 한다.
            playMissionSound('rejected');
          }
        }
      } catch (err) {
        showFeedback(buildScanErrorFeedback(err));
        playMissionSound('rejected');
      } finally {
        sendingRef.current = false;
        resetMissionFlow();
      }
    },
    [
      selectedSpot, showFirstMissionIntro,
      showFeedback, resetMissionFlow, playMissionSound,
    ]
  );

  // ── QR 스캔 → 미션 종류로 분기 ────────────────────────────
  const handleScan = useCallback(
    async (qrCode) => {
      if (!selectedSpot || busy || firstMissionIntro || phase === PHASE_MISSION) return;
      if (isCooldown(qrCode)) return;
      cooldownMap.current.set(qrCode, Date.now());
      setupRefreshIdleRef.current = false;

      // 직전 신규 완료 효과음이 남아 있더라도 다음 스캔의 결과음으로 오해하지 않게 즉시 정지.
      stopMissionSounds();
      setFeedback(null);
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);

      const mtype = selectedSpot.mission_type || 'qr';

      // 첫 수행형 미션은 러너를 거쳐야 한다. 그 외 첫 콤보 미션만 기존 즉시 태그 경로.
      if (shouldUseFirstMissionTagShortcut(selectedSpot)) {
        await sendTag(qrCode, null);
        return;
      }

      // qr: 즉시 태깅(사전조회 생략). 응답 already_tagged 로 D9 메시지.
      if (mtype === 'qr') {
        await sendTag(qrCode, null);
        return;
      }

      // ox_quiz / picture_quiz / funny_photo: check 선행 → 이미 참여면 미션 미실행.
      setBusy(true);
      try {
        const chk = await checkQuestSpot(qrCode, selectedSpot.id);
        if (chk && chk.already_tagged) {
          // 이미 참여 — 퀴즈/촬영 헛수고 방지(D9 ②/③).
          showFeedback(buildD9Feedback(selectedSpot.name, chk));
          playMissionSound('rejected');
          setBusy(false);
          return;
        }
        // 신규 — 미션 수행 단계로 진입.
        // OX 인사말 화면(②)용 별명. 서버가 공백뿐인 값을 이미 null 로 눕혀 보낸다(계약 C1).
        setPendingNickname(chk?.member_nickname ?? null);
        setPendingQr(qrCode);
        setPhase(PHASE_MISSION);
        setBusy(false);
      } catch (err) {
        showFeedback(buildScanErrorFeedback(err));
        playMissionSound('rejected');
        setBusy(false);
      }
    },
    [
      selectedSpot, busy, firstMissionIntro, phase, firstMissionSelected,
      sendTag, showFeedback, stopMissionSounds, playMissionSound,
    ]
  );

  // 미션 완료 콜백(ox/funny) → tag 전송(mission_version + mission_result)
  const handleMissionComplete = useCallback(
    (missionResult) => {
      if (!pendingQr || !selectedSpot) {
        resetMissionFlow();
        return;
      }
      sendTag(pendingQr, {
        mission_version: selectedSpot.mission_config_version,
        mission_result: missionResult,
      });
    },
    [pendingQr, selectedSpot, sendTag, resetMissionFlow]
  );

  // 미션 중단(돌아가기 / 카메라 불가) → 스캔 화면 복귀
  const handleMissionCancel = useCallback(() => {
    resetMissionFlow();
  }, [resetMissionFlow]);

  const handleOxQuizVideoAbort = useCallback((reason) => {
    reportQuestIntroVideoError({
      reason,
      spotId: selectedSpot ? selectedSpot.id : null,
      videoUrl: resolveMissionVideoUrl(selectedSpot, resolveCommonLogoIntroUrl(activeSet)),
    });
  }, [selectedSpot, activeSet]);

  const handleFunnyPhotoVideoAbort = useCallback((reason, videoUrl) => {
    reportQuestIntroVideoError({
      reason,
      spotId: selectedSpot ? selectedSpot.id : null,
      videoUrl,
    });
  }, [selectedSpot]);

  const handlePictureQuizVideoAbort = useCallback((reason) => {
    reportQuestIntroVideoError({
      reason,
      spotId: selectedSpot ? selectedSpot.id : null,
      videoUrl: resolvePictureQuizIntroUrl(
        selectedSpot,
        resolveCommonLogoIntroUrl(activeSet) || findSharedOxIntroVideoUrl(spots),
      ),
    });
  }, [selectedSpot, activeSet, spots]);

  // USB QR 리더기 / 네이티브 키오스크 스캔 — 태블릿 현장 기본 입력(카메라는 HTTPS 폴백).
  // 미션 수행 중(PHASE_MISSION)·통신 중·피드백 표시 중에는 비활성(중복 스캔 차단).
  // 🔴 `!adminOpen` 이 맨 앞이다 — 관리자 바가 떠 있으면 다른 조건과 무관하게 스캔을 멈춘다.
  //    이 항이 없어서 "미션선택 터치가 가끔 안 된다" 가 발생했다(2026-08-07).
  useUsbQrReader({
    enabled: !adminOpen
      && !!selectedSpot && !busy && !feedback && !firstMissionIntro && phase === PHASE_SCAN,
    onScan: (raw) => {
      // 🔴 퀘스트 전용 합성 파서 — 회원(JRG_)·밴드(ZLB_)에 더해 여권(ZLP_)까지 통과시킨다.
      //    공유 `extractQRCode` 는 넓히지 않는다(다른 8개 스캔화면 오염 방지 — S10).
      const code = extractQuestScanCode(raw);
      if (code) {
        handleScan(code);
        return;
      }
      // 🔴 파싱 실패를 조용히 넘기지 않는다(2026-07-30 요청). `extractQuestScanCode` 는
      //    `JRG_`(회원)·`ZLB_`(밴드)·`ZLP_`(여권)만 통과시키므로, 타사 QR·상품 바코드·
      //    구형 `JRB_` 밴드는 여기로 온다.
      //    이전에는 `if (code)` 로 끝나 **아무 화면도 뜨지 않아** 아이·운영자가 고장으로 오해했다.
      showFeedback(buildUnparsableFeedback());
      playMissionSound('rejected');
    },
  });

  const renderSpotCard = (spot) => {
    const selected = selectedSpot && selectedSpot.id === spot.id;
    return (
      <button
        key={spot.id}
        className={`qt-spot-card ${selected ? 'qt-spot-card-selected' : ''}`}
        onClick={() => handleSelectSpot(spot)}
        data-testid={`quest-spot-${spot.id}`}
      >
        <span className="qt-spot-name">{spot.name}</span>
        {spot.description && <span className="qt-spot-desc">{spot.description}</span>}
        {((spot.mission_type && spot.mission_type !== 'qr')
          || (spot.reward_type && spot.reward_type !== 'none') || selected) && (
          <span className="qt-spot-card-foot">
            {spot.mission_type && spot.mission_type !== 'qr' && (
              <span className="qt-spot-mission" data-testid={`quest-spot-mission-${spot.id}`}>
                {spot.mission_type === 'ox_quiz'
                  ? '🧠 OX 퀴즈'
                  : spot.mission_type === 'picture_quiz'
                    ? '🖼️ 그림 퀴즈'
                    : '📸 사진 찍기'}
              </span>
            )}
            {spot.reward_type && spot.reward_type !== 'none' && (
              <span className="qt-spot-mission" data-testid={`quest-spot-reward-${spot.id}`}>
                {spot.reward_type === 'coin'
                  ? `🪙 주렁주화 ${spot.reward_coin_amount ?? ''}개`
                  : `🎟️ ${spot.reward_coupon_name || '쿠폰'}`}
              </span>
            )}
            {selected && <span className="qt-spot-current">현재 선택</span>}
          </span>
        )}
      </button>
    );
  };

  // (구 renderSpotPicker 모달 제거 — 위 주석 참조. 관련 CSS `.qt-spot-modal*` 는 남아 있으나
  //  사용되지 않는다. 되살릴 때 그 스타일을 그대로 쓸 수 있어 지우지 않았다.)

  // ── 미션 수행 영역(ox/funny) 렌더 ──
  const renderMission = () => {
    const mtype = selectedSpot.mission_type;
    const config = selectedSpot.mission_config || null;
    const commonIntroVideoUrl = resolveCommonLogoIntroUrl(activeSet);

    if (mtype === 'ox_quiz') {
      return (
        <OxQuizMissionFlow
          spot={selectedSpot}
          config={config}
          commonIntroVideoUrl={commonIntroVideoUrl}
          spotName={selectedSpot.name}
          memberNickname={pendingNickname}
          onComplete={handleMissionComplete}
          onCancel={handleMissionCancel}
          onVideoAbort={handleOxQuizVideoAbort}
        />
      );
    }

    if (mtype === 'picture_quiz') {
      return (
        <PictureQuizMissionFlow
          spot={selectedSpot}
          config={config}
          sharedIntroVideoUrl={commonIntroVideoUrl || findSharedOxIntroVideoUrl(spots)}
          sharedVerdictConfig={findSharedOxVerdictConfig(spots)}
          spotName={selectedSpot.name}
          memberNickname={pendingNickname}
          onComplete={handleMissionComplete}
          onCancel={handleMissionCancel}
          onVideoAbort={handlePictureQuizVideoAbort}
        />
      );
    }

    if (mtype === 'funny_photo') {
      // 개발(http) 환경은 카메라 차단 → 미션 불가 안내(앱 안 깨짐). 운영(https)은 동작.
      if (CAMERA_BLOCKED) {
        return (
          <div className="qt-center-state qt-center-error" data-testid="quest-funny-camera-blocked">
            <span className="qt-center-icon">📷</span>
            <p className="qt-center-text">
              이 환경에서는 카메라를 쓸 수 없어요
              <br />
              (운영 HTTPS 태블릿에서 동작)
            </p>
            <button className="qt-btn qt-btn-retry" onClick={handleMissionCancel}>
              돌아가기
            </button>
          </div>
        );
      }
      // QR 대기부터 유지하는 아래 사진 미션 컴포넌트가 준비된 영상을 그대로 사용한다.
      return null;
    }

    // 알 수 없는 미션 종류 — 방어적으로 스캔 복귀.
    return (
      <div className="qt-center-state qt-center-error" data-testid="quest-mission-unknown">
        <span className="qt-center-icon">⚠️</span>
        <p className="qt-center-text">알 수 없는 미션 종류예요.</p>
        <button className="qt-btn qt-btn-retry" onClick={handleMissionCancel}>
          돌아가기
        </button>
      </div>
    );
  };

  // ── 렌더 ─────────────────────────────────────────────────
  const renderBody = () => {
    if (loading) {
      return (
        <div className="qt-center-state" data-testid="quest-tag-loading">
          <span className="qt-center-icon">⏳</span>
          <p className="qt-center-text">퀘스트 정보를 불러오는 중…</p>
        </div>
      );
    }

    if (setupError) {
      return (
        <div className="qt-center-state qt-center-error" data-testid="quest-tag-setup-error">
          <span className="qt-center-icon">⚠️</span>
          <p className="qt-center-text">{setupError}</p>
          <button className="qt-btn qt-btn-retry" onClick={loadSetup}>
            다시 시도
          </button>
        </div>
      );
    }

    if (!activeSet || spots.length === 0) {
      return (
        <div className="qt-center-state" data-testid="quest-tag-no-set">
          <span className="qt-center-icon">🌳</span>
          <p className="qt-center-text">진행 중인 퀘스트가 없습니다</p>
          <button className="qt-btn qt-btn-retry" onClick={loadSetup}>
            새로고침
          </button>
        </div>
      );
    }

    // 스팟 미선택 → 선택 화면
    if (!selectedSpot) {
      const setRewardLabel = formatQuestSetReward(activeSet);
      return (
        <div className="qt-select-state" data-testid="quest-tag-spot-select">
          <div className="qt-select-head">
            <h2 className="qt-select-title">이 태블릿의 퀘스트(미션)를 선택하세요</h2>
            <p className="qt-select-sub">
              {activeSet.name}
              {setRewardLabel ? ` · 보상: ${setRewardLabel}` : ''}
            </p>
          </div>
          <div className="qt-spot-grid">
            {spots.map(renderSpotCard)}
          </div>
        </div>
      );
    }

    // 미션 수행 중(ox/funny — 회원 식별 후) → 미션 러너 화면
    if (phase === PHASE_MISSION) {
      return (
        <div className="qt-mission-state" data-testid="quest-tag-mission">
          <div className="qt-mission-area">{renderMission()}</div>
        </div>
      );
    }

    // 스캔 단계 공통 오버레이(확인 중 / D9 피드백) — 두 스캔 화면이 같은 마크업을 쓴다.
    const scanOverlays = (
      <>
        {busy && !firstMissionSelected && (
          <div className="qt-overlay qt-overlay-busy">
            <div className="qt-spinner" />
            <p>확인 중…</p>
          </div>
        )}
        {/* 🔴 안내판(이미완료 / 등록되지않음)은 여기 있으면 안 된다 — 페이지 루트에서 렌더한다.
            `scanOverlays` 는 화면마다 다른 부모(.qt-scanner-area / .qt-cam-scan / .qt-ox-scan)에
            들어가는데, `.mnb` 는 position 기반이라 **그 부모 크기에 갇힌다**. 실제로 qr 일반
            화면에서 432×360 박스에 갇혀 본문이 잘렸다(2026-08-04). 아래 최상위 return 참조.
            그 밖의 피드백(신규 완료·회수된 밴드·미션타입 거부 등)은 기존 메시지 오버레이. */}
        {feedback && !feedback.notice && (
          <div
            className={`qt-overlay ${feedback.success ? 'qt-overlay-success' : 'qt-overlay-fail'} ${
              feedback.popup ? 'qt-overlay-popup' : ''
            }`}
            data-testid="quest-tag-feedback"
          >
            <p className="qt-feedback-msg">{feedback.message}</p>
          </div>
        )}
        {/* 🔴 분기 화면(지도)은 **페이지가 직접** 그린다. 영상/인사 컴포넌트 안으로 넣으면
            그쪽이 MissionStatusMap 을 import 해 책임이 섞이고 5초 타이머 소유자가 모호해진다.
            지도 조회가 실패했으면(`firstMissionMap === null`) 아무것도 그리지 않고,
            5초 타이머는 그대로 돌아 QR 대기로 복귀한다 — 아이를 무한 대기에 두지 않는다. */}
        {firstMissionIntro?.phase === 'map' && firstMissionMap && (
          <MissionStatusMap
            data={firstMissionMap}
            agentTitle={buildFirstMissionAgentTitle(firstMissionIntro.memberNickname)}
            bannerText={firstMissionBannerTextFor(firstMissionIntro.outcome)}
          />
        )}
        {/* 분기 3 — 전부 완료 (Figma 3698:70384). 5초 뒤 QR 대기로 돌아간다.
            지도와 마찬가지로 **페이지가 직접** 그린다(타이머 소유자를 한 곳에 둔다). */}
        {firstMissionIntro?.phase === 'complete' && (
          <div className="qt-first-mission-complete" data-testid="quest-first-mission-complete">
            <img className="qt-first-mission-complete-title" src={missionCompleteTitle} alt="" />
            <p className="qt-first-mission-complete-text">
              미션을 모두 완료했어요.
              <br />
              이제 본부로 고고!
            </p>
          </div>
        )}
        {firstMissionIntro && firstMissionIntro.phase !== 'map'
          && firstMissionIntro.phase !== 'complete' && (
          <FirstMissionIntro
            phase={firstMissionIntro.phase}
            memberNickname={firstMissionIntro.memberNickname}
            videoUrl={firstMissionIntro.videoUrl}
            onVideoFinished={handleFirstMissionIntroFinished}
            onVideoAbort={handleFirstMissionVideoAbort}
            routeVideoUrl={firstMissionIntro.routeVideoUrl}
            onRouteFinished={finishFirstMissionFlow}
            onRouteAbort={handleFirstMissionRouteAbort}
          />
        )}
      </>
    );

    // 첫 번째 콤보 미션은 종류와 관계없이 별도 장식 없는 검은 화면에서 QR 태깅을 기다린다.
    // 태깅 성공 시 20초 영상 → 3초 별명 인사 후 이 검은 첫 화면으로 복귀한다.
    // 두 번째 이후 미션만 아래의 카메라/OX 전용 안내 화면을 사용한다.

    // 카메라(funny_photo) 스팟의 스캔 대기 → 미션 안내판(Figma 2255:66132).
    // 제목=스팟명, 본문=스팟 설명(없으면 기본 안내). 입력 수단은 기존과 동일한 USB QR 리더기.
    // ⚠️ 옛 `사진촬영 미션` 배지는 2255:66132 에 없어 제거했다(간판 위 zoolung 로고가 그 역할).
    if (!firstMissionSelected && selectedSpot.mission_type === 'funny_photo') {
      return (
        <div className="qt-scan-state qt-cam-scan" data-testid="quest-tag-scan">
          <MissionWaitingScreen
            imageUrl={selectedSpot.waiting_screen_url}
            alt={`${selectedSpot.name || '카메라 미션'} 대기화면`}
          />
          {scanOverlays}
        </div>
      );
    }

    // OX 퀴즈(ox_quiz) 스팟의 스캔 대기 → 정글 배경 + 대형 타이틀(Figma 1966:43882).
    if (!firstMissionSelected && selectedSpot.mission_type === 'ox_quiz') {
      return (
        <div className="qt-scan-state qt-ox-scan" data-testid="quest-tag-scan">
          <MissionWaitingScreen
            imageUrl={selectedSpot.waiting_screen_url}
            alt={`${selectedSpot.name || 'OX 퀴즈'} 대기화면`}
          />
          {scanOverlays}
        </div>
      );
    }


    if (!firstMissionSelected && selectedSpot.mission_type === 'picture_quiz') {
      return (
        <div className="qt-scan-state qt-picture-scan" data-testid="quest-tag-scan">
          <MissionWaitingScreen
            imageUrl={selectedSpot.waiting_screen_url}
            alt={`${selectedSpot.name || '그림 퀴즈'} 대기화면`}
          />
          {scanOverlays}
        </div>
      );
    }

    // 그 외 미션(qr 등) → 기존 회원 QR 식별 화면
    return (
      <div className="qt-scan-state" data-testid="quest-tag-scan">
        <div className="qt-scanner-area">
          {/* 회원 식별은 USB QR 리더 기본 — 카메라 자동오픈 안 함(카메라는 funny_photo 미션에서만) */}
          {shouldShowQrScanPrompt(firstMissionSelected) && (
            <div className="qt-usb-prompt" data-testid="quest-tag-usb-prompt">
              {/* 일반 단순 태깅(qr) 화면에는 운영자가 미션을 식별할 수 있도록 이름을 표시한다.
                  첫 번째 미션은 이 안내 대신 아래 BASE CAMP 로고를 띄운다. */}
              <p className="qt-usb-mission" data-testid="quest-tag-mission-name">
                {selectedSpot.name}
              </p>
              <QrcodeOutlined className="qt-usb-icon" />
              <p className="qt-usb-text">QR 스캔</p>
            </div>
          )}
          {/* 첫 미션 대기 화면 — 검은 배경은 그대로 두고 마크 + CTA 문구 2행을 얹는다
              (Figma 3379:44765, 2026-08-20 재취득 — 같은 노드의 3차 개정).
              배경까지 바꾸는 개정이 아니다.
              🔴 문구는 반드시 이 조건 블록 안에 둔다 — 밖으로 나가면 일반 QR 미션 화면에도 샌다. */}
          {shouldShowFirstMissionLogo(firstMissionSelected) && (
            <>
              <span
                className="qt-first-mission-logo"
                role="img"
                aria-label="베이스캠프"
                data-testid="quest-first-mission-logo"
              />
              <p
                className="qt-first-mission-cta-title"
                data-testid="quest-first-mission-cta-title"
              >
                자, 이제 모험을 시작해 볼까?
              </p>
              <p
                className="qt-first-mission-cta-sub"
                data-testid="quest-first-mission-cta-sub"
              >
                리더기에 <em className="qt-first-mission-cta-em">QR코드</em>를 스캔하세요.
              </p>
            </>
          )}
          {scanOverlays}
        </div>
      </div>
    );
  };

  // 카메라 스팟 전용 전체화면 배경 — 두 단계가 서로 다른 그림을 쓴다.
  //   스캔 대기(PHASE_SCAN)  → 나무 안내판  (.qt-page-cam)
  //   촬영 중(PHASE_MISSION) → 정글 액자    (.qt-page-shoot)
  // renderBody 의 분기 조건과 동일해야 배경과 본문이 어긋나지 않는다.
  const isSpotReady =
    !loading && !setupError && !!activeSet && spots.length > 0 && !!selectedSpot;
  const isFirstMissionScanScreen =
    isSpotReady && firstMissionSelected && phase === PHASE_SCAN;
  const isCameraSpot = isSpotReady && selectedSpot.mission_type === 'funny_photo';
  const isCameraScanScreen = isCameraSpot && phase === PHASE_SCAN && !isFirstMissionScanScreen;
  // 카메라 차단(HTTP) 환경은 러너 대신 안내문만 뜨므로 전체화면으로 만들지 않는다.
  const isCameraShootScreen = isCameraSpot && phase === PHASE_MISSION && !CAMERA_BLOCKED;
  // OX 퀴즈 — 스캔 대기(걸린 간판, Figma 1966:43882) / 문항 화면(나무판, Figma 1966:43897)
  const isOxSpot = isSpotReady && selectedSpot.mission_type === 'ox_quiz';
  const isOxScanScreen = isOxSpot && phase === PHASE_SCAN && !isFirstMissionScanScreen;
  const isOxQuizScreen = isOxSpot && phase === PHASE_MISSION;
  const isPictureQuizScreen = isSpotReady
    && selectedSpot.mission_type === 'picture_quiz'
    && phase === PHASE_MISSION;
  const isPictureQuizScanScreen = isSpotReady
    && selectedSpot.mission_type === 'picture_quiz'
    && phase === PHASE_SCAN
    && !isFirstMissionScanScreen;
  const isCameraFullBleed =
    isFirstMissionScanScreen
    || isCameraScanScreen || isCameraShootScreen || isOxScanScreen
    || isOxQuizScreen || isPictureQuizScreen || isPictureQuizScanScreen;

  const pageModifier = isFirstMissionScanScreen
    ? ' qt-page-first-mission'
    : isCameraScanScreen
      ? ' qt-page-cam'
      : isCameraShootScreen
        ? ' qt-page-shoot'
        : isOxScanScreen
          ? ' qt-page-ox'
          : isOxQuizScreen
            ? ' qt-page-quiz'
            : isPictureQuizScanScreen
              ? ' qt-page-picture-quiz-scan'
              : isPictureQuizScreen
                ? ' qt-page-picture-quiz'
            : '';

  return (
    <div className={`qt-page${pageModifier}`}>
      {/* 🔴 상단 헤더 전면 제거(2026-07-30 요청) — 고객 앞 태블릿이라 조작 UI 를 노출하지 않는다.
          제거분: `← 메뉴` 버튼 · `🌿 퀘스트 미션` 제목 · 스팟명 트리거 · 지점명(StoreBadge).
          대체: 아래 관리자 바(오른쪽 아래 5회 터치 → 비밀번호 2580 → 뒤로가기/미션선택/미션변경).
          ⚠️ 헤더 탭으로 전체화면을 켜던 `toggleFullscreen` 도 함께 사라졌다 —
             실기기는 키오스크 앱(Device Owner)이 전체화면을 담당한다. */}
      <main className={`qt-content${isCameraFullBleed ? ' qt-content-cam' : ''}`}>
        {renderBody()}
        {isCameraSpot && !CAMERA_BLOCKED && (
          <FunnyPhotoMissionFlow
            key={`${selectedSpot.id}:${selectedSpot.mission_config_version}:${selectedSpot.mission_video_url}:${resolveCommonLogoIntroUrl(activeSet)}`}
            active={isCameraShootScreen}
            camera={photoCamera}
            spot={selectedSpot}
            config={selectedSpot.mission_config || null}
            commonIntroVideoUrl={resolveCommonLogoIntroUrl(activeSet)}
            memberNickname={pendingNickname}
            onComplete={handleMissionComplete}
            onCancel={handleMissionCancel}
            onVideoAbort={handleFunnyPhotoVideoAbort}
            onCameraError={() => { /* 촬영 오류는 러너의 재시도 화면에서 처리한다. */ }}
          />
        )}
      </main>
      {isCameraScanScreen && !busy && !feedback && (
        <PhotoCameraToolbar camera={photoCamera} onChange={changePhotoCamera} storageWarning={cameraStorageWarning} />
      )}

      {/* 🔴 전체화면 안내판은 **페이지 루트 직계**여야 한다 (2026-08-04 수정).
          `.mnb` 는 `position:fixed; inset:0` 인데, 이걸 `renderBody()` 안에 두면
          `.qt-scan-state` 의 `transform: translateY(-24px)` 가 **fixed 의 컨테이닝 블록**이 되어
          720px 상자 안에 갇힌다(transform 은 fixed 를 가둔다 — 실측 확인).
          `.qt-page` 에는 transform 이 없으므로 여기서만 뷰포트 전체를 덮는다.
          ⚠️ 여기를 `renderBody()` 안으로 되돌리면 회귀다 — QuestTagPage.notice.test.js 가 잡는다. */}
      {feedback && feedback.notice && (
        <MissionNoticeBoard notice={feedback.notice} rewardMsg={feedback.rewardMsg} />
      )}

      <QuestSpotAdminBar
        spots={spots}
        selectedSpot={selectedSpot}
        // 잠금 판정 + 사유를 한 값으로 넘긴다(null = 변경 가능). 바가 이 문구를 그대로 띄운다.
        lockReason={spotPickLockReason}
        // 보류 선택 확정은 기존 영속화 경로(handleSelectSpot)를 그대로 태운다 — 저장 로직 이중화 금지.
        onApplySpot={handleSelectSpot}
        onExitHome={() => navigate('/')}
        // setState 는 항등성이 안정적이라 바의 통지 effect 가 불필요하게 재실행되지 않는다.
        onOpenChange={setAdminOpen}
      />
    </div>
  );
}
